import {
	ListDataSource, MetadataDataSource, PqItemDataSource, ScoreDataSource,
	SettingsDataSource
} from "@sabyr-dev/mist_core/model/datasource";
import {
	AnswerValue, ContentItem, ItemType, PqItemUsage, UsageMode
} from "@sabyr-dev/mist_core/model/datasource/item";
import { ErrorReportResponse } from "@sabyr-dev/mist_core/model/datasource/settings";
import { Annotations } from "@sabyr-dev/mist_core/lib/userdata";
import {
	AttemptState, GlobalState,
	StateDataSource
} from "@sabyr-dev/mist_core/model/datasource/state";

export type Product = string;
export type Attempt = number;
export type Quiz = string;
export type Question = string;

export interface ElibraryContentItem {
	content: {
		content_id: number,
		hash: string,
		html: string,
		type: string,
		word: string
	},
	context?: {
		content: {
			content_id: number,
			hash: string,
			html: string,
			type: string,
			word: string
		},
		content_html: string,
		number: number,
		sections: {
			id: string,
			level: number,
			order: number,
			parent_id: string,
			title: string
		}[],
		style: string
	},
	generated: string,
	id: Question,
	images: {},
	multiplechoice?: {
		answers: {
			answer_id: number,
			content: string,
			correct: boolean,
			id: string,
			name: string
		}[],
		explanation: string,
		explanation_text: string,
		prompt: string,
		prompt_text: string,
		style: string
	},
	multiplecorrect?: {
		answers: {
			answer_id: number,
			content: string,
			correct: boolean,
			id: string,
			name: string
		}[],
		explanation: string,
		explanation_text: string,
		prompt: string,
		prompt_text: string,
		style: string
	},
	numeric?: {
		answers: {
			maximum?: {
				text: string,
				value: number
			},
			minimum?: {
				text: string,
				value: number
			},
			number: {
				text: string,
				value: number
			}
		},
		explanation: string,
		explanation_text: string,
		prompt: string,
		prompt_text: string,
		style: string
	},
	flashcard?: {
		question: string,
		question_text: string,
		answer: string,
		style: string,
	},
	name: string,
	number: number,
	sections: {
		id: string,
		level: number,
		number: number,
		order: number,
		parent_id: string,
		title: string
	}[],
	type: ItemType,
}

function getProductId(): Product | undefined {
	const pattern: RegExp = /practicequestion\/(?<type>products|test|result)\/(?:v\/)?(?<sku>[-\w.~%!$&'()*+,;=:@_]+)(?:\/(?<attempt>\d+))?/;
	const url: URL = new URL(window.location.href);
	const result: RegExpExecArray = pattern.exec(url.pathname);
	return result ? result.groups['sku'] : undefined;
}

function getAttemptId(): Attempt | undefined {
	const pattern: RegExp = /practicequestion\/(?<type>products|test|result)\/(?:v\/)?(?<sku>[-\w.~%!$&'()*+,;=:@_]+)(?:\/(?<attempt>\d+))?/;
	const url: URL = new URL(window.location.href);
	const result: RegExpExecArray = pattern.exec(url.pathname);
	if (result) {
		const value = parseInt(result.groups['attempt']);
		if (!Number.isNaN(value)) { return value; }
	}
	return undefined;
}

export class ElibraryMetadataDataSource extends MetadataDataSource<Product, Attempt> {
	getProductId(): Promise<Product> {
		return new Promise<string>((resolve, reject) => {
			const result = getProductId();
			if (result) { resolve(result); }
			else { reject(); }
		});
	}

	getAttempt(): Promise<Attempt> {
		return new Promise<number>((resolve, reject) => {
			const result = getAttemptId();
			if (result) { resolve(result); }
			else { reject(); }
		});
	}

	getInitialTime(): Promise<number> {
		return new Promise<number>(resolve => {
			const timer: Element = document.querySelector('.timer .input-group-text');
			if (!timer) { resolve(0); return; }
			const time: string = timer.textContent;
			const match: RegExpExecArray = /(((?<hours>\d+):)?(?<minutes>\d+):)?(?<seconds>[\d.]+)/.exec(time);
			if (match) {
				const hours: number = parseInt(match.groups.hours || '0');
				const minutes: number = parseInt(match.groups.minutes || '0');
				const seconds: number = parseFloat(match.groups.seconds);
				const total: number = (hours * 3600 + minutes * 60 + seconds) * 1000;
				resolve(total);
			}
			else { resolve(0); }
		});
	}
}

export class ElibraryListDataSource extends ListDataSource<Question> { }

interface ElibraryFinishedType {
	result?: string;
}

export class ElibraryItemDataSource extends PqItemDataSource<Product, Attempt, Question> {
	getItem(product: Product, question: Question, randomize: boolean): Promise<ContentItem<Question>> {
		const { cw_sku, set_id, cw_id } = getCWMeta(question);
		return fetch(`/practicequestion/question/${product}/${cw_sku}/${set_id}/${cw_id}`)
			.then((response: Response) => {
				if (!response.ok) { return Promise.reject(response); }
				return response.json() as Promise<ElibraryContentItem>;
			})
			.then((question: ElibraryContentItem) => {
				if (question.type === 'multiplechoice' && randomize) {
					question.multiplechoice.answers = question.multiplechoice.answers
						.reduce((
							result: { answer_id: number; content: string; correct: boolean; id: string; name: string }[],
							answer: { answer_id: number; content: string; correct: boolean; id: string; name: string }
						) => {
							result.push(answer);
							const index: number = Math.trunc(
								Math.random() * result.length
							);
							const temp: any = result[index];
							result[index] = result[result.length - 1];
							result[result.length - 1] = temp;
							return result;
						}, []);
				}
				const {context, ...content} = question;
				return {
					...content,
					section_title: content.sections[content.sections.length - 1].title,
					contexts: (context ? [{
						...context,
						id: context.content.content_id.toString()
					}] : []),
				};
			});
	}

	setUserData(product: Product, attempt: Attempt, question: Question, data: Annotations): Promise<AnswerValue<Attempt, Question>> {
		const { cw_sku, set_id, cw_id } = getCWMeta(question);
		return fetch(
		`/practicequestion/answer/${product}/${cw_sku}/${set_id}/${cw_id}/${attempt}`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ answer: data.answer })
			}
		).then((response: Response) => response.json() as Promise<AnswerValue<Attempt, Question>>);
	}

	setPing(product: Product, attempt: Attempt, mode: UsageMode, times: PqItemUsage<Question>[]): Promise<void> {
		return fetch(
			`/practicequestion/ping/${product}/${attempt}`,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					review: mode === "pq_review",
					times: times.map((record: PqItemUsage<Question>) => {
						const { cw_sku, set_id, cw_id } = getCWMeta(record.item.id);
						return ({
							start: record.time.start,
							end: record.time.end,
							context_viewed: Object.values(
								record.time.extras.contexts_viewed
							)[0] ?? false,
							question_id: cw_id,
							cw_sku, set_id,
						});
					})
				})
			}
		).then(() => {});
	}

	setFinished(product: Product, attempt: Attempt): Promise<string> {
		return fetch(
			`/practicequestion/finish/${product}/${attempt}`,
			{
				method: 'POST',
				redirect: 'manual'
			}
		).then((response: Response) => response.json() as Promise<ElibraryFinishedType>)
		.then((response: ElibraryFinishedType) => response.result);
	}
}

export function getCWMeta(question: Question): { cw_sku: string, set_id: string, cw_id: string } {
	const selector = `.item[data-id='${question}']`;
	const element = document.querySelector<HTMLElement>(selector);
	return {
		cw_sku: element?.dataset.cw_sku,
		set_id: element?.dataset.set,
		cw_id: element?.dataset.cw_id,
	};
}

export class ElibraryScoreDataSource extends ScoreDataSource<Attempt, Quiz> { }

export class ElibrarySettingsDataSource extends SettingsDataSource<Product> {
	sendErrorReport(product: Product, form: HTMLFormElement): Promise<ErrorReportResponse> {
		return fetch(
			'/report/',
			{
				method: 'POST',
				body: new FormData(form)
			}
		).then((response: Response) => response.json() as Promise<ErrorReportResponse>)
		.then((response: ErrorReportResponse) => response)
		.catch((err: string) => ({ success: false, reason: err } as ErrorReportResponse));
	}

	getLocalStoragePrefix(): RegExp {
		return /^elibraryMist/;
	}
}

function getCookie(): string | undefined {
	return document.cookie.split(';')
		.map(cookie => cookie.split('=').map(part => part.trim()))
		.map(([key, value]) => ({key, value}))
		.find(cookie => cookie.key == 'elibrary-flash')
		?.value;
}

export class ElibraryGlobalStateDataSource extends StateDataSource<GlobalState> {
	getStateKey(): string {
		return `elibraryMist.${getCookie()}`;
	}
}

export class ElibraryAttemptStateDataSource extends StateDataSource<AttemptState> {
	getStateKey(): string {
		return `elibraryMist.${getCookie()}.${getProductId()}.${getAttemptId()}`;
	}
}

